import { setdefault } from "../util/util";
import ActionControl from "./ActionControl";
import Icon from "./Icon";
import PortalMenu from "./PortalMenu";
import ToolbarDropdown from "./ToolbarDropdown";
import ToolbarIcon from "./ToolbarIcon";
import { IconButton } from "./TooltipControls";
import update from "immutability-helper";
import * as _ from "lodash";
import * as PropTypes from "prop-types";
import * as React from "react";
import { ButtonGroup, MenuItem } from "react-bootstrap";
import ReactDOM from "react-dom";
function genTooltip(t, show, ns) {
    return typeof show === "string" ? t(show, { ns }) : undefined;
}
class MenuAction extends React.PureComponent {
    render() {
        const { t, action, id } = this.props;
        return (React.createElement(MenuItem, { eventKey: id, onSelect: this.trigger, disabled: action.show !== true, title: genTooltip(t, action.show, action.options?.namespace) },
            React.createElement(Icon, { name: action.icon }),
            React.createElement("div", { className: "button-text" }, t(action.title, { ns: action.options?.namespace }))));
    }
    trigger = () => {
        const { action, instanceId } = this.props;
        const instanceIds = typeof instanceId === "string" ? [instanceId] : instanceId;
        action.action(instanceIds);
    };
}
function nop() {
    // nop
}
function IconBarIcon(props) {
    const { t, actionId, buttonType, icon, instanceIds, tooltipPlacement } = props;
    // stuff for submenus
    const [open, setOpenMenu] = React.useState(false);
    const [subMenus, setSubMenus] = React.useState([]);
    const setOpen = React.useCallback((value) => {
        if (Array.isArray(icon.subMenus)) {
            setSubMenus(icon.subMenus);
        }
        else {
            setSubMenus(icon.subMenus());
        }
        setOpenMenu(value);
    }, [setOpenMenu, icon.subMenus]);
    const itemRef = React.useRef();
    const trigger = React.useCallback(() => {
        icon.action?.(instanceIds, icon.data);
        if (icon.subMenus !== undefined) {
            setOpen((old) => !old);
        }
    }, [instanceIds, icon, setOpen]);
    const setOpenFalse = React.useCallback(() => {
        setOpen(false);
    }, [setOpen]);
    const hasIcon = buttonType === undefined || ["icon", "both", "menu"].includes(buttonType);
    const hasText = buttonType === undefined || ["text", "both", "menu"].includes(buttonType);
    const tooltip = typeof icon.show === "string"
        ? icon.show
        : t(icon.title, { ns: icon.options?.namespace });
    const setItemRef = (ref) => {
        itemRef.current = ReactDOM.findDOMNode(ref);
    };
    return (React.createElement(ToolbarIcon, { key: actionId, ref: setItemRef, className: actionId, instanceId: instanceIds, icon: hasIcon ? icon.icon : undefined, text: hasText ? t(icon.title, { ns: icon.options?.namespace }) : undefined, tooltip: tooltip, onClick: trigger, placement: tooltipPlacement, disabled: icon.show !== true && icon.show !== undefined, stroke: icon.options?.hollowIcon === true, hollow: icon.options?.hollowIcon === true },
        React.createElement(PortalMenu, { open: open, target: itemRef.current, onClose: setOpenFalse, onClick: nop }, (subMenus ?? []).map((subMenu) => (React.createElement(MenuAction, { t: t, key: subMenu.title, id: subMenu.title, action: subMenu, instanceId: actionId }))))));
}
/**
 * represents an extensible row of icons/buttons/actions
 * In the simplest form this is simply a bunch of buttons that will run
 * an action if clicked, but an icon can also be more dynamic (i.e. rendering
 * dynamic content or having multiple states)
 *
 * @class IconBar
 * @extends {ComponentEx<IProps, {}>}
 */
class IconBar extends React.Component {
    static contextTypes = {
        menuLayer: PropTypes.object,
    };
    portalTargetRef;
    mBackgroundClick;
    constructor(props) {
        super(props);
        this.state = {
            open: false,
        };
        this.updateBGClick();
    }
    UNSAFE_componentWillReceiveProps() {
        this.updateBGClick();
    }
    render() {
        const { actions, collapse, icon, id, groupByIcon, orientation, className, style, } = this.props;
        const classes = [];
        if (className) {
            classes.push(className);
        }
        if (collapse) {
            classes.push("btngroup-collapsed");
            const collapsed = [];
            const unCollapsed = [];
            actions.forEach((action) => {
                if (collapse === "force" ||
                    action.options === undefined ||
                    !action.options.noCollapse) {
                    collapsed.push(action);
                }
                else {
                    unCollapsed.push(action);
                }
            });
            const moreButton = collapsed.length === 0 ? null : (React.createElement("div", null,
                React.createElement(IconButton, { id: `btn-menu-${id}`, className: "btn-embed", onClick: this.toggleOpen, tooltip: "", icon: icon || "menu", stroke: true, ref: this.setPortalTargetRef }),
                React.createElement(PortalMenu, { open: this.state.open, target: this.portalTargetRef, onClose: this.toggleOpen, onClick: this.toggleOpen }, this.state.open ? collapsed.map(this.renderMenuItem) : null)));
            return (React.createElement(ButtonGroup, { id: id, className: classes.join(" "), style: style },
                moreButton,
                unCollapsed.map((iter, idx) => (React.createElement("div", { key: idx }, this.renderIcon(iter, idx))))));
        }
        else {
            const groupedByIcon = actions.reduce((prev, action, idx) => {
                if (action.icon !== undefined && groupByIcon !== false) {
                    setdefault(prev, action.icon, []).push(action);
                }
                else {
                    prev[idx.toString()] = [action];
                }
                return prev;
            }, {});
            const byFirstPrio = (lhs, rhs) => {
                return lhs[0].position - rhs[0].position;
            };
            const groupByGroup = (prev, actionList) => {
                const group = actionList[0].group ?? "__unset";
                if (prev[group] === undefined) {
                    prev[group] = [actionList];
                }
                else {
                    prev[group].push(actionList);
                }
                return prev;
            };
            const grouped = Object.values(groupedByIcon)
                .sort(byFirstPrio)
                .reduce(groupByGroup, {});
            return (React.createElement(ButtonGroup, { id: id, className: classes.join(" "), style: style, vertical: orientation === "vertical", onClick: this.mBackgroundClick },
                this.props.children,
                Object.keys(grouped).length !== 1
                    ? Object.keys(grouped).map((groupId, idx) => (React.createElement("div", { className: "iconbar-actiongroup", key: groupId }, grouped[groupId].map(this.renderIcons))))
                    : Object.values(grouped)[0].map(this.renderIcons)));
        }
    }
    renderMenuItem = (icon, index) => {
        const { t, instanceId } = this.props;
        const id = `${instanceId || "1"}_${index}`;
        if (icon.icon === null && icon.component === undefined) {
            return (React.createElement(MenuItem, { className: "menu-separator-line", key: id, disabled: true }, t(icon.title, { ns: icon.options?.namespace })));
        }
        if (icon.icon !== undefined) {
            return (React.createElement(MenuAction, { key: id, id: id, action: icon, instanceId: instanceId, t: t }));
        }
        else {
            return (React.createElement(MenuItem, { key: id, eventKey: id, disabled: icon.show !== true, title: genTooltip(t, icon.show) }, this.renderCustomIcon(id, icon)));
        }
    };
    renderIcon = (icon, index) => {
        if (icon.icon === null && icon.component === undefined) {
            // skip text-only elements in this mode
            return null;
        }
        return this.renderIconInner(icon, index);
    };
    renderIcons = (icons, index) => {
        if (icons.length === 1) {
            if (icons[0].icon === null && icons[0].component === undefined) {
                // skip text-only elements in this mode
                return null;
            }
            return this.renderIconInner(icons[0], index);
        }
        else {
            return this.renderIconGroup(icons, index);
        }
    };
    renderIconGroup = (icons, index) => {
        const { t, instanceId, orientation, buttonType } = this.props;
        const instanceIds = typeof instanceId === "string" ? [instanceId] : instanceId;
        const id = `${instanceId || "1"}_${index}`;
        return (React.createElement(ToolbarDropdown, { t: t, key: id, id: id, instanceId: instanceIds, icons: icons, buttonType: buttonType, orientation: orientation }));
    };
    renderIconInner = (icon, index, forceButtonType) => {
        const { t, instanceId, tooltipPlacement } = this.props;
        const instanceIds = typeof instanceId === "string" ? [instanceId] : instanceId;
        let actionId = (icon.title || index.toString())
            .toLowerCase()
            .replace(/ /g, "-");
        actionId = `action-${actionId}`;
        if (icon.component === undefined) {
            return (React.createElement(IconBarIcon, { key: actionId, t: t, actionId: actionId, buttonType: forceButtonType || this.props.buttonType, icon: icon, instanceIds: instanceIds, tooltipPlacement: tooltipPlacement ?? "top" }));
        }
        else {
            return this.renderCustomIcon(actionId, icon);
        }
    };
    renderCustomIcon(id, icon) {
        // custom case. the caller can pass properties via the props() function and by
        // passing the prop to the iconbar. the props on the iconbar that we don't handle are
        // passed on
        const knownProps = [
            "condition",
            "className",
            "group",
            "t",
            "i18nLoadedAt",
            "objects",
            "children",
        ];
        const unknownProps = Object.keys(this.props).reduce((prev, current) => {
            if (knownProps.indexOf(current) === -1) {
                return {
                    ...prev,
                    [current]: this.props[current],
                };
            }
            else {
                return prev;
            }
        }, {});
        const staticProps = {
            ...unknownProps,
            key: id,
            buttonType: this.props.buttonType,
            orientation: this.props.orientation,
        };
        if (icon.props !== undefined) {
            const addProps = icon.props();
            return (React.createElement(icon.component, { ...staticProps, ...addProps, parentType: "iconbar" }));
        }
        else {
            return React.createElement(icon.component, { ...staticProps, parentType: "iconbar" });
        }
    }
    setPortalTargetRef = (ref) => {
        this.portalTargetRef = ref;
    };
    toggleOpen = () => {
        this.setState(update(this.state, {
            open: { $set: !this.state.open },
        }));
    };
    updateBGClick() {
        const { actions, clickAnywhere, instanceId } = this.props;
        const instanceIds = typeof instanceId === "string" ? [instanceId] : instanceId;
        this.mBackgroundClick =
            clickAnywhere === true && actions.length === 1
                ? (evt) => {
                    // don't trigger if the button itself was clicked
                    if (!evt.isDefaultPrevented()) {
                        evt.preventDefault();
                        actions[0].action(instanceIds);
                    }
                }
                : undefined;
    }
}
class ActionIconBar extends React.Component {
    static ACTION_PROPS = [
        "filter",
        "group",
        "instanceId",
        "showAll",
        "staticElements",
    ];
    render() {
        const actionProps = _.pick(this.props, ActionIconBar.ACTION_PROPS);
        const barProps = _.omit(this.props, ActionIconBar.ACTION_PROPS);
        return (React.createElement(ActionControl, { ...actionProps },
            React.createElement(IconBar, { ...barProps })));
    }
}
export default ActionIconBar;
